import React from 'react'
import { NextSeo } from 'next-seo'
import NextHead from 'next/head'
import { getFirstNotEmpty } from '@corratech/pylot-utils'
import { Organization } from './Organization'
import { SearchBox } from './SearchBox'
import { useStoreConfig } from '@config/hooks/useStoreConfig'

export type BaseSeoProps = {
    titleOverride?: string
    asPath: string
    canonicalUrl?: string
    descriptionOverride?: string
    noFollow?: boolean
}

export const BaseSeo = ({
    titleOverride,
    asPath,
    canonicalUrl,
    descriptionOverride,
    noFollow
}: BaseSeoProps): JSX.Element => {
    const { seo } = useStoreConfig()
    const { title, titleTemplate, description, twitter, openGraph } =
        seo.default

    const noFollowArray = [{ name: 'robots', content: 'NOINDEX, NOFOLLOW' }]

    return (
        <>
            {/* Default data. Can be overridden by model components, if specific models are provided */}
            <NextSeo
                title={getFirstNotEmpty([titleOverride, title])}
                description={getFirstNotEmpty([
                    descriptionOverride,
                    description
                ])}
                titleTemplate={titleTemplate ?? undefined}
                twitter={twitter ?? undefined}
                openGraph={openGraph ?? undefined}
                {...(canonicalUrl ? { canonical: canonicalUrl } : {})}
                additionalMetaTags={noFollow ? noFollowArray : []}
            />

            {/* NextSeo does not allow setting both <title> and <meta name="title" ...> (a known bug) */}
            <NextHead>
                <meta name="title" content={String(title)} />
                <meta name="test" content={canonicalUrl} />
                <link rel="icon" href="/favicon.ico" />
                <link
                    rel="apple-touch-icon"
                    href="/meta-icons/apple-touch-icon.png"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href="/meta-icons/favicon-32x32.png"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="16x16"
                    href="/meta-icons/favicon-16x16.png"
                />
            </NextHead>

            <SearchBox asPath={asPath} />
            <Organization asPath={asPath} />
        </>
    )
}
